import React, { useMemo, useState, useEffect } from 'react';
import { SpaProps } from '@nab/x-spa-react';
import { useActions } from '@nab/nab-x-react';
import { GlobalStyle, Theme } from '@nab/nui-react';
import { ApolloProvider } from 'react-apollo';
import MobileMenu from './Navbar/MobileMenu';

import { safeGet } from '../utils/tools';
import { getApolloClient } from '../utils/graphql/ApolloClient';

import { SpaWrapper, MobileWrapper } from './common/styles';
import { SpaFooter } from './Footer/SpaFooter';
import { MiniAppEvents } from '../utils/common';
import { AnonymousMainApp, LoggedInMainApp } from './MainApp';

const VERSION_ALIAS_FLAG = 'miniapp-versionalias-external-developer-shell';
const DEFAULT_VERSION = 'latest';
const CANARY_FLAG_KEY = 'miniapp-canary-external-developer-shell-latest';

// x-runtime registers the shell's LaunchDarkly flag strategy under the reserved appId 'SHELL'
// (see @nab/x-runtime initRuntime: rootInstance.appMetaData.id === 'SHELL', and the runtime log
// "added flag strategy for appId: SHELL"). Re-scope the user to 'SHELL' so flag reads / readiness /
// subscriptions always target the scope that actually owns the initialised strategy, independent of
// whatever appId this SPA instance was forked under. Also avoids the pre-registration render race
// where early renders (before the strategy is added) see status 'unknown' / areFlagsReady() false.
const SHELL_FLAG_APP_ID = 'SHELL';

// Never block the canary effect forever: whenFlagsReady() only resolves once a strategy is added for
// the queried appId, so it can hang if that never happens. Cap the wait so the effect always proceeds
// (falling back to flag defaults on timeout) instead of leaving the subscription/re-eval stuck.
const FLAGS_READY_TIMEOUT_MS = 5000;

const raceWithTimeout = (promise: Promise<unknown> | undefined | null, ms: number): Promise<unknown> => {
    if (promise == null || typeof promise.then !== 'function') {
        return Promise.resolve();
    }
    let timer: number;
    const timeout = new Promise<void>(resolve => {
        timer = window.setTimeout(resolve, ms);
    });
    return Promise.race([promise, timeout]).finally(() => window.clearTimeout(timer));
};

// Extracted to module scope so the canary useEffect's subscription setup/teardown stays shallow
// (avoids nesting the change/no-op callbacks more than 4 levels deep inside the effect).
type CanaryTailoring = { toggleOnChangeFlag?: (key: string, cb: () => void, mode: 'on' | 'off') => void } | null | undefined;

const subscribeCanaryFlag = (tailoring: CanaryTailoring, onChange: () => void): void => {
    tailoring?.toggleOnChangeFlag?.(CANARY_FLAG_KEY, onChange, 'on');
};

const unsubscribeCanaryFlag = (tailoring: CanaryTailoring): void => {
    tailoring?.toggleOnChangeFlag?.(CANARY_FLAG_KEY, () => {}, 'off');
};

const PortalSpa = ({ context, activeAppInstance, actions, appConfig, user }: SpaProps) => {
    if (process.env.NODE_ENV === 'development') {
        console.log('PortalSpa rerender :: ', context, activeAppInstance, appConfig);
    }
    const fullRoute = window.location.pathname;
    const appPath = safeGet(() => appConfig.route.ownPath);
    const activeRoute = safeGet(() => fullRoute.split(appPath)[1]);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [returnedError401, setReturnedError401] = useState(false);
    const [tokenConfig, setTokenConfig] = useState(null);
    const { authenticate, addEventListener, removeEventListener } = useActions();

    // The LD flag strategy lives under the reserved 'SHELL' scope, NOT this SPA's forked appId.
    // Re-scope the user so flag reads / whenFlagsReady / subscriptions hit the initialised strategy.
    // Falls back to the SPA-scoped tailoring when forkForApp is unavailable (e.g. tests / older SDKs).
    const shellTailoring = useMemo(() => {
        try {
            const forkable = user as unknown as {
                forkForApp?: (app: { appMetaData: { id: string } }) => { tailoring?: typeof user.tailoring } | undefined;
            };
            return forkable?.forkForApp?.({ appMetaData: { id: SHELL_FLAG_APP_ID } })?.tailoring ?? user?.tailoring;
        } catch {
            return user?.tailoring;
        }
    }, [user]);

    // Reads the version-alias LD flag to determine whether this shell is targeting a canary BFF.
    // Bypasses the outer platform's assetManifestUrl guard that prevents versionAliasResolver
    // from ever evaluating the versionalias flag on the shell.
    const resolveCanaryForApollo = () => {
        if (!shellTailoring) return false;
        const versionAlias = shellTailoring.checkFlag(VERSION_ALIAS_FLAG, DEFAULT_VERSION) ?? DEFAULT_VERSION;
        if (!versionAlias.endsWith('-canary')) return false;
        return shellTailoring.checkFlag(CANARY_FLAG_KEY, false) ?? false;
    };

    const [isCanaryForApollo, setIsCanaryForApollo] = useState(() => resolveCanaryForApollo());

    useEffect(() => {
        let cancelled = false;
        let teardown: (() => void) | null = null;

        const initCanary = async () => {
            // Wait for LD to finish its async network call before reading any flags, but never block
            // forever: whenFlagsReady() hangs indefinitely when the queried appId has no registered
            // strategy. On timeout we proceed and fall back to flag defaults so the effect can still
            // set up the change subscription rather than being permanently stuck.
            await raceWithTimeout(shellTailoring?.whenFlagsReady?.(), FLAGS_READY_TIMEOUT_MS);
            if (cancelled) return;

            setIsCanaryForApollo(resolveCanaryForApollo());

            const versionAlias = shellTailoring?.checkFlag(VERSION_ALIAS_FLAG, DEFAULT_VERSION) ?? DEFAULT_VERSION;
            if (!versionAlias.endsWith('-canary')) return;

            const handleCanaryChange = () => setIsCanaryForApollo(resolveCanaryForApollo());
            subscribeCanaryFlag(shellTailoring, handleCanaryChange);
            teardown = () => unsubscribeCanaryFlag(shellTailoring);
        };

        initCanary();

        return () => {
            cancelled = true;
            teardown?.();
        };
    }, [shellTailoring]);

    console.log('Shell logs: LD flags ready:', shellTailoring?.areFlagsReady?.());
    console.log('Shell logs: LD client status:', shellTailoring?.getFlagStrategyStatus?.());
    console.log('Shell logs: miniapp-versionalias-external-developer-shell =', shellTailoring?.checkFlag?.('miniapp-versionalias-external-developer-shell', 'NON-EXISTENT'));
    console.log('Shell logs: miniapp-canary-external-developer-shell-latest (direct) =', isCanaryForApollo);

    function eventListener(eventData: boolean) {
        setReturnedError401(eventData);
    }

    useEffect(() => {
        setReturnedError401(false);
    }, [activeRoute]);

    useEffect(() => {
        addEventListener(MiniAppEvents.UNAUTHORISED_ERROR, eventListener);
        return () => {
            removeEventListener(MiniAppEvents.UNAUTHORISED_ERROR, eventListener);
        };
    });

    useEffect(() => {
        (async () => {
            try {
                setTokenConfig(await authenticate());
            } catch {
                console.log('Error: Unable to get token');
            }
        })();
    }, [authenticate]);

    const client = useMemo(() => {
        console.log('Shell logs: Apollo using canary BFF:', isCanaryForApollo);
        if (tokenConfig) {
            return getApolloClient(tokenConfig && tokenConfig['tokenValue'], isCanaryForApollo);
        }
    }, [tokenConfig, isCanaryForApollo]);

    if (!client) {
        return <>loading</>;
    }

    const MemoMobileMenu = () => (
        <>
            {useMemo(
                () => (
                    <MobileMenu
                        activeRoute={activeRoute}
                        items={(context && context.menu) || []}
                        baseRoute={activeAppInstance && appConfig && appConfig.route ? appConfig.route.matchedPath : '/'}
                        navigate={actions && actions.navigate}
                        actions={actions}
                    />
                ),
                [],
            )}
        </>
    );

    const MemoFooter = () => (
        <>
            {useMemo(
                () => (
                    <SpaFooter activeRoute={activeRoute} actions={actions} />
                ),
                [],
            )}
        </>
    );

    const mainAppContent = tokenConfig?.['tokenValue'] ? (
        <ApolloProvider client={client}>
            <LoggedInMainApp
                activeRoute={activeRoute}
                context={context}
                activeAppInstance={activeAppInstance}
                actions={actions}
                appConfig={appConfig}
                user={user}
                setMobileMenuOpen={setMobileMenuOpen}
                mobileMenuOpen={mobileMenuOpen}
                returnedError401={returnedError401}
            />
        </ApolloProvider>
    ) : (
        <AnonymousMainApp
            activeRoute={activeRoute}
            context={context}
            activeAppInstance={activeAppInstance}
            actions={actions}
            appConfig={appConfig}
            user={user}
            setMobileMenuOpen={setMobileMenuOpen}
            mobileMenuOpen={mobileMenuOpen}
        />
    );

    return (
        <>
            <GlobalStyle normalize borderBox />
            <Theme>
                <MobileWrapper className={mobileMenuOpen ? 'has-mobile-open' : ''}>
                    <MemoMobileMenu />
                    <SpaWrapper>
                        {mainAppContent}
                    </SpaWrapper>
                    <MemoFooter />
                </MobileWrapper>
            </Theme>
        </>
    );
};

export default PortalSpa;
